import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

/**
 * PUBLIC, unauthenticated invoice viewer.
 *
 * Backs the "Click here to view your bill" link sent to tenants over
 * SMS / WhatsApp / Email. Tenants have no dashboard login, so this page
 * must work with no token. It reads ?ref=INV-XXXXXXXXXX and renders a
 * clean, printable bill from the public API endpoint.
 */
const PublicInvoice = ({ location }) => {
  const [invoice, setInvoice] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error

  const search = (location && location.search) || window.location.search || '';
  const ref = new URLSearchParams(search).get('ref') || '';

  useEffect(() => {
    let active = true;
    (async () => {
      if (!ref) {
        setStatus('error');
        return;
      }
      try {
        const { data: { response } } = await axios.get(
          `${process.env.REACT_APP_API_URL}public/invoice?ref=${encodeURIComponent(ref)}`,
        );
        if (!active) return;
        if (response && response.invoiceNumber) {
          setInvoice(response);
          setStatus('ready');
        } else {
          setStatus('error');
        }
      } catch (e) {
        if (active) setStatus('error');
      }
    })();
    return () => { active = false; };
  }, [ref]);

  // maximumFractionDigits caps display at 2dp — a bare toLocaleString()
  // lets any float noise in upstream arithmetic leak straight through.
  const numOpts = { maximumFractionDigits: 2 };
  const money = (n) => `${invoice ? invoice.currency : 'KES'} ${Number(n || 0).toLocaleString(undefined, numOpts)}`;
  const units = (n) => Number(n || 0).toLocaleString(undefined, numOpts);

  const wrap = {
    minHeight: '100vh',
    background: '#f4f5f7',
    padding: '24px 12px',
    fontFamily: 'Arial, Helvetica, sans-serif',
    color: '#333',
  };
  const card = {
    maxWidth: 640,
    margin: '0 auto',
    background: '#fff',
    border: '1px solid #e6e6e6',
    borderRadius: 6,
    overflow: 'hidden',
  };

  if (status === 'loading') {
    return (
      <div style={wrap}>
        <div style={{ ...card, padding: 40, textAlign: 'center', color: '#888' }}>
          Loading your bill&hellip;
        </div>
      </div>
    );
  }

  if (status === 'error' || !invoice) {
    return (
      <div style={wrap}>
        <div style={{ ...card, padding: 40, textAlign: 'center' }}>
          <h3 style={{ color: '#c0392b', marginTop: 0 }}>Bill not available</h3>
          <p style={{ color: '#666' }}>
            We couldn&apos;t find this bill. The link may be incorrect or expired.
            Please contact your property manager for assistance.
          </p>
        </div>
      </div>
    );
  }

  const title = invoice.type === 'Extra Charge' && invoice.chargeName
    ? invoice.chargeName : invoice.type;

  return (
    <div style={wrap}>
      <div style={card}>
        {/* Header */}
        <div style={{ background: '#c0392b', color: '#fff', padding: '20px 24px' }}>
          <div style={{ fontSize: 13, opacity: 0.85 }}>{invoice.propertyName}</div>
          <div style={{ fontSize: 22, fontWeight: 'bold', marginTop: 2 }}>{title}</div>
          <div style={{ fontSize: 13, opacity: 0.9, marginTop: 4 }}>
            {invoice.invoiceNumber} &nbsp;|&nbsp; {invoice.period}
          </div>
        </div>

        {/* Bill-to */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #eee' }}>
          <div style={{ fontSize: 12, color: '#999', textTransform: 'uppercase' }}>Billed To</div>
          <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 2 }}>
            {invoice.tenantName || '—'}
          </div>
          {invoice.houseNo && (
            <div style={{ fontSize: 13, color: '#666' }}>House / Unit: {invoice.houseNo}</div>
          )}
        </div>

        {/* Line items */}
        <div style={{ padding: '18px 24px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#999', fontSize: 12 }}>
                <th style={{ padding: '6px 0' }}>Description</th>
                <th style={{ padding: '6px 0', textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.charges && invoice.charges.length > 0 ? (
                invoice.charges.map((c, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '8px 0' }}>{c.type}</td>
                    <td style={{ padding: '8px 0', textAlign: 'right' }}>{money(c.amount)}</td>
                  </tr>
                ))
              ) : (
                <tr style={{ borderTop: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '8px 0' }}>{title}</td>
                  <td style={{ padding: '8px 0', textAlign: 'right' }}>
                    {money(invoice.totalAmount)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {invoice.reading && (
            <div style={{
              marginTop: 16,
              background: '#f8f9fa',
              border: '1px solid #eee',
              borderRadius: 4,
              padding: '12px 14px',
              fontSize: 13,
            }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: 6 }}>Meter Reading</div>
              <div>Current Reading: {units(invoice.reading.current)}</div>
              <div>Previous Reading: {units(invoice.reading.previous)}</div>
              <div>Consumed Units: {units(invoice.reading.consumed)}</div>
              <div>Rate: {units(invoice.reading.rate)}</div>
            </div>
          )}

          {invoice.description && (
            <div style={{ marginTop: 12, fontSize: 12, color: '#777' }}>
              {invoice.description}
            </div>
          )}
        </div>

        {/* Totals */}
        <div style={{ padding: '0 24px 20px' }}>
          <div style={{
            borderTop: '2px solid #c0392b',
            paddingTop: 14,
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 15,
          }}
          >
            <span>Total</span>
            <strong>{money(invoice.totalAmount)}</strong>
          </div>
          <div style={{
            display: 'flex', justifyContent: 'space-between', fontSize: 15, marginTop: 8,
          }}
          >
            <span>Balance Due</span>
            <strong style={{ color: invoice.balance > 0 ? '#c0392b' : '#27ae60' }}>
              {money(invoice.balance)}
            </strong>
          </div>
          <div style={{ marginTop: 10, fontSize: 12, color: '#999' }}>
            Status: <span style={{ textTransform: 'capitalize' }}>
              {String(invoice.status || '').replace(/_/g, ' ')}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          background: '#fafafa',
          borderTop: '1px solid #eee',
          padding: '14px 24px',
          fontSize: 12,
          color: '#888',
          textAlign: 'center',
        }}
        >
          Kindly settle this bill on time to avoid any inconvenience. Thank you.
        </div>
      </div>

      <div style={{ maxWidth: 640, margin: '14px auto 0', textAlign: 'center' }} className="no-print">
        <button
          type="button"
          onClick={() => window.print()}
          style={{
            background: '#c0392b',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            padding: '10px 22px',
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Print / Save as PDF
        </button>
      </div>

      <style>
        {'@media print { .no-print { display: none !important; } body { background: #fff; } }'}
      </style>
    </div>
  );
};

PublicInvoice.propTypes = {
  location: PropTypes.oneOfType([PropTypes.object]),
};

PublicInvoice.defaultProps = {
  location: null,
};

export default PublicInvoice;
